import React, { useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Button,
  VStack,
  HStack,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Textarea,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  Tooltip,
  Grid,
  Avatar,
  SimpleGrid,
  Heading,
  Container,
  Checkbox,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  getAppointmentDetails, 
  updateConsultantNotes 
} from '../services/appointmentService';
import { 
  uploadFileForAppointment, 
  uploadFileForAppointmentAdmin,
  downloadFile,
  deleteFile 
} from '../services/fileService';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  FaDownload, 
  FaTrash, 
  FaUpload, 
  FaEdit, 
  FaSave,
  FaTimes,
  FaUser,
  FaCalendarAlt,
  FaClock,
  FaFileAlt,
  FaStickyNote,
  FaPhone,
  FaEnvelope,
  FaUserTie,
  FaIdCard,
  FaMoneyBillWave,
  FaCreditCard,
  FaPlus,
  FaFile,
  FaTag,
  FaCommentAlt,
  FaCloudUploadAlt,
  FaArrowLeft,
  FaGlobe
} from "react-icons/fa";
import JalaliDateDisplay from '../components/JalaliDateDisplay';

function AppointmentDetails() {
  const { id: appointmentId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isConsultant, isManager } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const fileInputRef = useRef(null);
  
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const bgHover = useColorModeValue("gray.50", "gray.600");
  const primaryColor = useColorModeValue("teal.500", "teal.300");
  const secondaryColor = useColorModeValue("blue.500", "blue.300");

  // State for file upload
  const [uploadData, setUploadData] = useState({
    customName: '',
    description: '',
    public: false
  });
  const [isUploading, setIsUploading] = useState(false);

  // State for consultant notes
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [consultantNotes, setConsultantNotes] = useState('');

  // React Query hooks
  const queryClient = useQueryClient();

  // دریافت جزئیات ملاقات
  const {
    data: appointmentData,
    isLoading,
    error
  } = useQuery(
    ['appointment-details', appointmentId],
    () => getAppointmentDetails(appointmentId),
    {
      enabled: !!appointmentId,
      staleTime: 2 * 60 * 1000, // 2 دقیقه
    }
  );

  // Mutation برای به‌روزرسانی یادداشت مشاور
  const updateNotesMutation = useMutation(updateConsultantNotes, {
    onSuccess: () => {
      toast.success('یادداشت مشاور با موفقیت به‌روزرسانی شد');
      queryClient.invalidateQueries(['appointment-details', appointmentId]);
      setIsEditingNotes(false);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در به‌روزرسانی یادداشت مشاور');
    }
  });

  // Mutation برای آپلود فایل
  const uploadFileMutation = useMutation(
    (fileData) => {
      if (isAdmin()) {
        return uploadFileForAppointmentAdmin(
          appointmentData.data.appointment.client._id,
          appointmentId,
          fileData
        );
      } else {
        return uploadFileForAppointment(
          appointmentData.data.appointment.client._id,
          appointmentId,
          fileData
        );
      }
    },
    {
      onSuccess: () => {
        toast.success('فایل با موفقیت آپلود شد');
        queryClient.invalidateQueries(['appointment-details', appointmentId]);
        onClose();
        setUploadData({ customName: '', description: '', public: false });
      },
      onError: (error) => {
        toast.error(error.message || 'خطا در آپلود فایل');
      }
    }
  );

  // Mutation برای دانلود فایل
  const downloadFileMutation = useMutation(downloadFile, {
    onSuccess: (blob, fileName) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در دانلود فایل');
    }
  });

  // Mutation برای حذف فایل
  const deleteFileMutation = useMutation(deleteFile, {
    onSuccess: () => {
      toast.success('فایل با موفقیت حذف شد');
      queryClient.invalidateQueries(['appointment-details', appointmentId]);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در حذف فایل');
    }
  });

  // تنظیم یادداشت مشاور اولیه
  React.useEffect(() => {
    if (appointmentData?.data?.appointment?.consultantNotes) {
      setConsultantNotes(appointmentData.data.appointment.consultantNotes);
    }
  }, [appointmentData]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const fileData = {
        file,
        customName: uploadData.customName,
        description: uploadData.description,
        public: uploadData.public
      };
      await uploadFileMutation.mutateAsync(fileData);
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      await downloadFileMutation.mutateAsync(fileId);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (window.confirm('آیا از حذف این فایل اطمینان دارید؟')) {
      try {
        await deleteFileMutation.mutateAsync(fileId);
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  const handleSaveNotes = async () => {
    try {
      await updateNotesMutation.mutateAsync({
        appointmentId,
        consultantNotes
      });
    } catch (error) {
      console.error('Save notes error:', error);
    }
  };

  const handleCancelNotes = () => {
    setConsultantNotes(appointmentData?.data?.appointment?.consultantNotes || '');
    setIsEditingNotes(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      case 'no-show': return 'orange';
      case 'scheduled': return 'blue';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'تکمیل شده';
      case 'cancelled': return 'لغو شده';
      case 'no-show': return 'عدم حضور';
      case 'scheduled': return 'برنامه‌ریزی شده';
      case 'pending': return 'در انتظار';
      default: return status;
    }
  };

  const getPaymentColor = (paymentStatus) => {
    switch (paymentStatus) {
      case 'paid': return 'green';
      case 'partial': return 'yellow';
      case 'pending': return 'red';
      default: return 'gray';
    }
  };

  const getPaymentText = (paymentStatus) => {
    switch (paymentStatus) {
      case 'paid': return 'پرداخت شده';
      case 'partial': return 'نیمه پرداخت';
      case 'pending': return 'در انتظار پرداخت';
      default: return paymentStatus;
    }
  };

  if (isLoading) {
    return (
      <Box p={4}>
        <Flex justify="center" align="center" minH="400px">
          <VStack spacing={4}>
            <Spinner size="xl" color={primaryColor} />
            <Text color={textColor} fontSize="lg">در حال بارگذاری جزئیات ملاقات...</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>خطا!</AlertTitle>
            <AlertDescription>
              {error.message || 'خطا در دریافت اطلاعات ملاقات'}
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  const appointment = appointmentData?.data?.appointment;
  
  if (!appointment) {
    return (
      <Box p={4}>
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>هشدار!</AlertTitle>
            <AlertDescription>
              ملاقات مورد نظر یافت نشد
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Check permissions
  const canViewFiles = true; // همه کاربران می‌توانند فایل‌های عمومی را ببینند
  const canViewNotes = (isConsultant() && appointment.consultant._id === user.id) || isManager();
  const canEditNotes = isConsultant() && appointment.consultant._id === user.id;
  const canUploadFiles = isManager() || isConsultant();



  return (
    <Container maxW="7xl" p={4}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box bg={bgCard} p={6} borderRadius="xl" boxShadow="lg" border="1px" borderColor={borderColor}>
          <Flex justify="space-between" align="center" mb={4}>
            <HStack spacing={4}>
              <IconButton
                icon={<FaArrowLeft />}
                variant="outline"
                colorScheme="teal"
                onClick={() => navigate(-1)}
                aria-label="بازگشت"
              />
              <VStack align="start" spacing={1}>
                <Heading size="lg" color={textColor}>
                  جزئیات ملاقات
                </Heading>
                <Text color="gray.500" fontSize="sm">
                  شماره ملاقات: {appointment._id}
                </Text>
              </VStack>
            </HStack>
            <Badge
              colorScheme={getStatusColor(appointment.status)}
              fontSize="md"
              p={3}
              borderRadius="full"
            >
              {getStatusText(appointment.status)}
            </Badge>
          </Flex>
        </Box>

        {/* Main Content Grid */}
        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6}>
          {/* Left Column - Main Information */}
          <VStack spacing={6} align="stretch">
            {/* Appointment Information Card */}
            <Box bg={bgCard} p={6} borderRadius="xl" boxShadow="lg" border="1px" borderColor={borderColor}>
              <Heading size="md" color={textColor} mb={4} display="flex" alignItems="center">
                <FaCalendarAlt style={{ marginLeft: '8px' }} />
                اطلاعات ملاقات
              </Heading>
              
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <VStack align="start" spacing={4}>
                  <HStack spacing={3}>
                    <FaCalendarAlt color={primaryColor} />
                    <Box>
                      <Text fontSize="sm" color="gray.500">تاریخ</Text>
                      <JalaliDateDisplay
                        date={appointment.date}
                        format="jYYYY/jMM/jDD"
                        textProps={{
                          fontSize: "md",
                          fontWeight: "bold",
                          color: textColor
                        }}
                      />
                    </Box>
                  </HStack>

                  <HStack spacing={3}>
                    <FaClock color={primaryColor} />
                    <Box>
                      <Text fontSize="sm" color="gray.500">ساعت</Text>
                      <Text fontSize="md" fontWeight="bold" color={textColor}>
                        {appointment.time}
                      </Text>
                    </Box>
                  </HStack>

                  <HStack spacing={3}>
                    <FaClock color={primaryColor} />
                    <Box>
                      <Text fontSize="sm" color="gray.500">مدت زمان</Text>
                      <Text fontSize="md" fontWeight="bold" color={textColor}>
                        {appointment.duration} دقیقه
                      </Text>
                    </Box>
                  </HStack>
                </VStack>

                <VStack align="start" spacing={4}>
                  <HStack spacing={3}>
                    <FaMoneyBillWave color={primaryColor} />
                    <Box>
                      <Text fontSize="sm" color="gray.500">هزینه</Text>
                      <Text fontSize="md" fontWeight="bold" color={textColor}>
                        {appointment.cost?.toLocaleString() || 0} تومان
                      </Text>
                    </Box>
                  </HStack>

                  <HStack spacing={3}>
                    <FaCreditCard color={primaryColor} />
                    <Box>
                      <Text fontSize="sm" color="gray.500">وضعیت پرداخت</Text>
                      <Badge
                        colorScheme={getPaymentColor(appointment.paymentStatus)}
                        fontSize="sm"
                      >
                        {getPaymentText(appointment.paymentStatus)}
                      </Badge>
                    </Box>
                  </HStack>

                  <HStack spacing={3}>
                    <FaFileAlt color={primaryColor} />
                    <Box>
                      <Text fontSize="sm" color="gray.500">شماره پرونده</Text>
                      <Text fontSize="md" fontWeight="bold" color={textColor}>
                        {appointment.fileNumber || 'ثبت نشده'}
                      </Text>
                    </Box>
                  </HStack>
                </VStack>
              </SimpleGrid>

              {/* Companion Information - Only for Consultants and Managers */}
              {(isConsultant() || isManager()) && (
                <Box mt={6} p={4} bg="blue.50" borderRadius="lg" border="1px" borderColor="blue.200">
                  <Text fontSize="sm" color="blue.600" mb={3} fontWeight="semibold">
                    اطلاعات همراه
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <HStack spacing={3}>
                      <FaUser color="blue.500" />
                      <Box>
                        <Text fontSize="sm" color="blue.600">نام و نام خانوادگی همراه</Text>
                        <Text fontSize="md" fontWeight="bold" color={textColor}>
                          {appointment.companionName || 'ثبت نشده'}
                        </Text>
                      </Box>
                    </HStack>
                    <HStack spacing={3}>
                      <FaUserTie color="blue.500" />
                      <Box>
                        <Text fontSize="sm" color="blue.600">نسبت</Text>
                        <Text fontSize="md" fontWeight="bold" color={textColor}>
                          {appointment.companionRelation || 'ثبت نشده'}
                        </Text>
                      </Box>
                    </HStack>
                  </SimpleGrid>
                </Box>
              )}

              {appointment.notes && (
                <Box mt={6} p={4} bg="gray.50" borderRadius="lg">
                  <Text fontSize="sm" color="gray.500" mb={2}>یادداشت‌ها</Text>
                  <Text color={textColor}>{appointment.notes}</Text>
                </Box>
              )}
            </Box>

            {/* Client Information Card */}
            <Box bg={bgCard} p={6} borderRadius="xl" boxShadow="lg" border="1px" borderColor={borderColor}>
              <Heading size="md" color={textColor} mb={4} display="flex" alignItems="center">
                <FaUser style={{ marginLeft: '8px' }} />
                اطلاعات مراجع
              </Heading>
              
              <HStack spacing={6} align="start">
                <Avatar
                  size="lg"
                  name={`${appointment.client.firstName} ${appointment.client.lastName}`}
                  bg={primaryColor}
                />
                <VStack align="start" spacing={3} flex={1}>
                  <HStack spacing={3}>
                    <FaUser color={primaryColor} />
                    <Box>
                      <Text fontSize="sm" color="gray.500">نام و نام خانوادگی</Text>
                      <Text fontSize="md" fontWeight="bold" color={textColor}>
                        {appointment.client.firstName} {appointment.client.lastName}
                      </Text>
                    </Box>
                  </HStack>

                  <HStack spacing={3}>
                    <FaIdCard color={primaryColor} />
                    <Box>
                      <Text fontSize="sm" color="gray.500">کد ملی</Text>
                      <Text fontSize="md" fontWeight="bold" color={textColor}>
                        {appointment.client.nationalId || 'ثبت نشده'}
                      </Text>
                    </Box>
                  </HStack>

                                     {/* شماره تماس مراجع فقط برای مدیران نمایش داده می‌شود */}
                   {(isAdmin() || isManager()) && (
                     <HStack spacing={3}>
                       <FaPhone color={primaryColor} />
                       <Box>
                         <Text fontSize="sm" color="gray.500">شماره تماس</Text>
                         <Text fontSize="md" fontWeight="bold" color={textColor}>
                           {appointment.client.phone || 'ثبت نشده'}
                         </Text>
                       </Box>
                     </HStack>
                   )}
                </VStack>
              </HStack>
            </Box>

            {/* Consultant Information Card */}
            <Box bg={bgCard} p={6} borderRadius="xl" boxShadow="lg" border="1px" borderColor={borderColor}>
              <Heading size="md" color={textColor} mb={4} display="flex" alignItems="center">
                <FaUserTie style={{ marginLeft: '8px' }} />
                اطلاعات مشاور
              </Heading>
              
              <HStack spacing={6} align="start">
                <Avatar
                  size="lg"
                  name={appointment.consultant.name}
                  bg={secondaryColor}
                />
                <VStack align="start" spacing={3} flex={1}>
                  <HStack spacing={3}>
                    <FaUserTie color={secondaryColor} />
                    <Box>
                      <Text fontSize="sm" color="gray.500">نام مشاور</Text>
                      <Text fontSize="md" fontWeight="bold" color={textColor}>
                        {appointment.consultant.name}
                      </Text>
                    </Box>
                  </HStack>

                  {appointment.consultant.email && (
                    <HStack spacing={3}>
                      <FaEnvelope color={secondaryColor} />
                      <Box>
                        <Text fontSize="sm" color="gray.500">ایمیل</Text>
                        <Text fontSize="md" fontWeight="bold" color={textColor}>
                          {appointment.consultant.email}
                        </Text>
                      </Box>
                    </HStack>
                  )}

                  {appointment.consultant.phone && (
                    <HStack spacing={3}>
                      <FaPhone color={secondaryColor} />
                      <Box>
                        <Text fontSize="sm" color="gray.500">شماره تماس</Text>
                        <Text fontSize="md" fontWeight="bold" color={textColor}>
                          {appointment.consultant.phone}
                        </Text>
                      </Box>
                    </HStack>
                  )}
                </VStack>
              </HStack>
            </Box>
          </VStack>

          {/* Right Column - Notes and Files */}
          <VStack spacing={6} align="stretch">
            {/* Consultant Notes Card */}
            {(canViewNotes || isManager()) && (
              <Box bg={bgCard} p={6} borderRadius="xl" boxShadow="lg" border="1px" borderColor={borderColor}>
                <Flex align="center" justify="space-between" mb={4}>
                  <Heading size="md" color={textColor} display="flex" alignItems="center">
                    <FaStickyNote style={{ marginLeft: '8px' }} />
                    یادداشت مشاور
                  </Heading>
                  {(canEditNotes || isManager()) && !isEditingNotes && (
                    <IconButton
                      icon={<FaEdit />}
                      size="sm"
                      colorScheme="teal"
                      variant="outline"
                      onClick={() => setIsEditingNotes(true)}
                      aria-label="ویرایش یادداشت"
                    />
                  )}
                </Flex>

                {isEditingNotes ? (
                  <VStack spacing={4}>
                    <Textarea
                      value={consultantNotes}
                      onChange={(e) => setConsultantNotes(e.target.value)}
                      placeholder="یادداشت مشاور را وارد کنید..."
                      rows={8}
                      resize="vertical"
                    />
                    <HStack spacing={2} w="full">
                      <Button
                        leftIcon={<FaSave />}
                        colorScheme="teal"
                        onClick={handleSaveNotes}
                        isLoading={updateNotesMutation.isLoading}
                        flex={1}
                      >
                        ذخیره
                      </Button>
                      <Button
                        leftIcon={<FaTimes />}
                        variant="outline"
                        onClick={handleCancelNotes}
                        flex={1}
                      >
                        لغو
                      </Button>
                    </HStack>
                  </VStack>
                ) : (
                  <Box p={4} bg="gray.50" borderRadius="lg" minH="120px">
                    <Text color={textColor} whiteSpace="pre-wrap">
                      {appointment.consultantNotes || 'یادداشتی ثبت نشده است'}
                    </Text>
                  </Box>
                )}
              </Box>
            )}

            {/* Files Section - Upload Button for Admin */}
            {isAdmin() && !isManager() && (
              <Box bg={bgCard} p={6} borderRadius="xl" boxShadow="lg" border="1px" borderColor={borderColor}>
                <Flex align="center" justify="space-between" mb={4}>
                  <Heading size="md" color={textColor} display="flex" alignItems="center">
                    <FaFileAlt style={{ marginLeft: '8px' }} />
                    مدیریت فایل‌ها
                  </Heading>
                  <Button
                    leftIcon={<FaPlus />}
                    colorScheme="teal"
                    size="sm"
                    onClick={onOpen}
                  >
                    افزودن فایل
                  </Button>
                </Flex>
                <Box
                  p={8}
                  textAlign="center"
                  border="2px dashed"
                  borderColor={borderColor}
                  borderRadius="lg"
                >
                  <FaFileAlt size={48} color="gray" style={{ margin: '0 auto 16px' }} />
                  <Text color="gray.500" fontSize="lg" mb={2}>
                    مدیر می‌تواند فایل آپلود کند
                  </Text>
                  <Text color="gray.400" fontSize="sm">
                    لیست فایل‌ها فقط برای مشاوران و مدیر کل قابل مشاهده است
                  </Text>
                </Box>
              </Box>
            )}

            {/* Files Section - Full List for Consultants */}
            {canViewFiles && (
              <Box bg={bgCard} p={6} borderRadius="xl" boxShadow="lg" border="1px" borderColor={borderColor}>
                <Flex align="center" justify="space-between" mb={4}>
                  <Heading size="md" color={textColor} display="flex" alignItems="center">
                    <FaFileAlt style={{ marginLeft: '8px' }} />
                    فایل‌های ملاقات
                  </Heading>
                  {canUploadFiles && (
                    <Button
                      leftIcon={<FaPlus />}
                      colorScheme="teal"
                      size="sm"
                      onClick={onOpen}
                    >
                      افزودن فایل
                    </Button>
                  )}
                </Flex>

                {appointment.files && appointment.files.length > 0 ? (
                  <VStack spacing={3} align="stretch">
                    {appointment.files.map((file) => (
                      <Box
                        key={file._id}
                        p={4}
                        border="1px"
                        borderColor={borderColor}
                        borderRadius="lg"
                        _hover={{ bg: bgHover }}
                        transition="all 0.2s"
                      >
                        <Flex justify="space-between" align="center">
                          <VStack align="start" spacing={1} flex={1}>
                            <HStack spacing={2} align="center">
                              <Text fontWeight="bold" color={textColor}>
                                {file.customName || file.originalName}
                              </Text>
                              {file.public && (
                                <Badge colorScheme="green" size="sm">
                                  عمومی
                                </Badge>
                              )}
                              {!file.public && (
                                <Badge colorScheme="gray" size="sm">
                                  خصوصی
                                </Badge>
                              )}
                            </HStack>
                            {file.description && (
                              <Text fontSize="sm" color="gray.500" noOfLines={1}>
                                {file.description}
                              </Text>
                            )}
                            <HStack spacing={4} fontSize="sm" color="gray.500">
                              <Text>{(file.fileSize / 1024 / 1024).toFixed(2)} MB</Text>
                              <Text>•</Text>
                              <Text>آپلود شده توسط: {file.uploadedBy?.name || 'نامشخص'}</Text>
                              <Text>•</Text>
                              <JalaliDateDisplay
                                date={file.createdAt}
                                format="jYYYY/jMM/jDD"
                              />
                            </HStack>
                          </VStack>
                          <HStack spacing={2}>
                            <Tooltip label="دانلود">
                              <IconButton
                                icon={<FaDownload />}
                                size="sm"
                                colorScheme="blue"
                                variant="outline"
                                onClick={() => handleDownloadFile(file._id, file.originalName)}
                                isLoading={downloadFileMutation.isLoading}
                                aria-label="دانلود فایل"
                              />
                            </Tooltip>
                            {(isManager() || isConsultant()) && (
                              <Tooltip label="حذف">
                                <IconButton
                                  icon={<FaTrash />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="outline"
                                  onClick={() => handleDeleteFile(file._id)}
                                  isLoading={deleteFileMutation.isLoading}
                                  aria-label="حذف فایل"
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Flex>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Box
                    p={8}
                    textAlign="center"
                    border="2px dashed"
                    borderColor={borderColor}
                    borderRadius="lg"
                  >
                    <FaFileAlt size={48} color="gray" style={{ margin: '0 auto 16px' }} />
                    <Text color="gray.500" fontSize="lg" mb={2}>
                      هیچ فایلی برای این ملاقات آپلود نشده است
                    </Text>
                    {canUploadFiles && (
                      <Button
                        leftIcon={<FaUpload />}
                        colorScheme="teal"
                        variant="outline"
                        onClick={onOpen}
                        mt={2}
                      >
                        افزودن اولین فایل
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </VStack>
        </Grid>
      </VStack>

      {/* File Upload Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent mx={4} borderRadius="xl" boxShadow="2xl">
          <ModalHeader 
            bg={primaryColor} 
            color="white" 
            borderTopRadius="xl"
            display="flex"
            alignItems="center"
            fontSize="lg"
            fontWeight="bold"
          >
            <FaUpload style={{ marginLeft: '8px' }} />
            آپلود فایل جدید
          </ModalHeader>
          <ModalCloseButton color="white" />
          
          <ModalBody p={6}>
            <VStack spacing={6}>
              {/* File Selection Area */}
              <FormControl>
                <FormLabel 
                  color={textColor} 
                  fontWeight="semibold" 
                  mb={3}
                  display="flex"
                  alignItems="center"
                >
                  <FaFile style={{ marginLeft: '6px' }} />
                  انتخاب فایل *
                </FormLabel>
                <Box
                  border="2px dashed"
                  borderColor={uploadData.file ? primaryColor : "gray.300"}
                  borderRadius="xl"
                  p={6}
                  textAlign="center"
                  bg={uploadData.file ? "teal.50" : "gray.50"}
                  transition="all 0.3s"
                  cursor="pointer"
                  _hover={{
                    borderColor: primaryColor,
                    bg: "teal.50"
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
                    display="none"
                  />
                  
                  {uploadData.file ? (
                    <VStack spacing={3}>
                      <FaFileAlt size={32} color={primaryColor} />
                      <Text fontWeight="bold" color={primaryColor}>
                        {uploadData.file.name}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {(uploadData.file.size / 1024 / 1024).toFixed(2)} MB
                      </Text>
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="teal"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        تغییر فایل
                      </Button>
                    </VStack>
                  ) : (
                    <VStack spacing={3}>
                      <FaCloudUploadAlt size={40} color="gray" />
                      <Text fontSize="lg" fontWeight="semibold" color="gray.600">
                        فایل خود را انتخاب کنید
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        یا فایل را اینجا بکشید
                      </Text>
                      <Text fontSize="xs" color="gray.400">
                        فرمت‌های مجاز: PDF, DOC, DOCX, JPG, PNG, GIF, TXT
                      </Text>
                    </VStack>
                  )}
                </Box>
              </FormControl>

              {/* Custom Name Field */}
              <FormControl>
                <FormLabel 
                  color={textColor} 
                  fontWeight="semibold"
                  display="flex"
                  alignItems="center"
                >
                  <FaTag style={{ marginLeft: '6px' }} />
                  نام اختصاصی (اختیاری)
                </FormLabel>
                <Input
                  value={uploadData.customName}
                  onChange={(e) => setUploadData({ ...uploadData, customName: e.target.value })}
                  placeholder="نام اختصاصی برای فایل وارد کنید"
                  borderRadius="lg"
                  border="2px"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: primaryColor,
                    boxShadow: `0 0 0 1px ${primaryColor}`
                  }}
                />
              </FormControl>

              {/* Description Field */}
              <FormControl>
                <FormLabel 
                  color={textColor} 
                  fontWeight="semibold"
                  display="flex"
                  alignItems="center"
                >
                  <FaCommentAlt style={{ marginLeft: '6px' }} />
                  توضیحات (اختیاری)
                </FormLabel>
                <Textarea
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  placeholder="توضیحات مربوط به فایل را وارد کنید"
                  rows={4}
                  borderRadius="lg"
                  border="2px"
                  borderColor="gray.200"
                  resize="vertical"
                  _focus={{
                    borderColor: primaryColor,
                    boxShadow: `0 0 0 1px ${primaryColor}`
                  }}
                />
              </FormControl>

              {/* Public File Field */}
              <FormControl>
                <FormLabel 
                  color={textColor} 
                  fontWeight="semibold"
                  display="flex"
                  alignItems="center"
                >
                  <FaGlobe style={{ marginLeft: '6px' }} />
                  وضعیت فایل
                </FormLabel>
                <Box
                  p={4}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="lg"
                  bg="gray.50"
                >
                  <HStack spacing={3}>
                    <Checkbox
                      isChecked={uploadData.public}
                      onChange={(e) => setUploadData({ ...uploadData, public: e.target.checked })}
                      colorScheme="teal"
                      size="lg"
                    >
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="semibold" color={textColor}>
                          فایل عمومی
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          این فایل برای همه کاربران قابل مشاهده خواهد بود
                        </Text>
                      </VStack>
                    </Checkbox>
                  </HStack>
                </Box>
              </FormControl>

              {/* Upload Progress */}
              {isUploading && (
                <Box 
                  w="full" 
                  p={4} 
                  bg="teal.50" 
                  borderRadius="lg" 
                  border="1px" 
                  borderColor="teal.200"
                >
                  <Flex align="center" justify="center" direction="column" spacing={3}>
                    <Spinner size="lg" color="teal.500" thickness="4px" />
                    <Text mt={2} fontWeight="semibold" color="teal.600">
                      در حال آپلود فایل...
                    </Text>
                    <Text fontSize="sm" color="teal.500">
                      لطفاً صبر کنید
                    </Text>
                  </Flex>
                </Box>
              )}
            </VStack>
          </ModalBody>

          {/* Modal Footer */}
          <Box p={6} borderTop="1px" borderColor="gray.200">
            <HStack spacing={3} justify="flex-end">
              <Button
                variant="outline"
                onClick={onClose}
                isDisabled={isUploading}
                borderRadius="lg"
                px={6}
              >
                لغو
              </Button>
              <Button
                colorScheme="teal"
                onClick={() => {
                  if (uploadData.file) {
                    // ساخت FormData برای آپلود
                    const formData = new FormData();
                    formData.append('file', uploadData.file);
                    formData.append('customName', uploadData.customName);
                    formData.append('description', uploadData.description);
                    formData.append('public', uploadData.public); // اضافه کردن فیلد public
                    formData.append('clientId', appointment.client._id);
                    formData.append('appointmentId', appointment._id);
                    
                    // اجرای آپلود
                    uploadFileMutation.mutate(formData);
                  }
                }}
                isLoading={isUploading}
                isDisabled={!uploadData.file}
                leftIcon={<FaUpload />}
                borderRadius="lg"
                px={6}
              >
                آپلود فایل
              </Button>
            </HStack>
          </Box>
        </ModalContent>
      </Modal>
    </Container>
  );
}

export default AppointmentDetails;
